import { useEffect, useState } from 'react';
import { api, type Plan, type Tenant } from '../api';
import { useToast } from '../ui/Toast';
import { PageHeader } from '../ui/bits';

interface User {
  id: string;
  email: string;
  name: string;
  realm: string;
  isActive: boolean;
}

export function Admin() {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const toast = useToast();

  const load = () =>
    Promise.all([
      api.get<Plan[]>('/api/admin/plans').then(setPlans),
      api.get<User[]>('/api/admin/users').then(setUsers),
      api.get<Tenant[]>('/api/admin/tenants').then(setTenants),
    ]).catch(console.error);
  useEffect(() => {
    void load();
  }, []);

  const createTenant = async (form: FormData) => {
    try {
      await api.post('/api/admin/tenants', {
        slug: String(form.get('slug')),
        displayName: String(form.get('displayName')),
        packKey: String(form.get('packKey')),
        planKey: String(form.get('planKey')) || undefined,
      });
      toast.success('Tenant created.');
      void load();
    } catch {
      toast.error('Tenant creation failed — is the pack synced and the slug unique?');
    }
  };

  const createUser = async (form: FormData) => {
    try {
      await api.post('/api/admin/users', {
        email: String(form.get('email')),
        name: String(form.get('name')),
        realm: String(form.get('realm')),
      });
      toast.success('User created.');
      void load();
    } catch {
      toast.error('User creation failed.');
    }
  };

  return (
    <div className="page-stack">
      <PageHeader title="Admin" sub={`${tenants.length} tenants · ${plans.length} plans · ${users.length} users`} />

      <div className="panel">
        <h2>New tenant (from pack)</h2>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void createTenant(new FormData(e.currentTarget));
          }}
          className="row"
        >
          <input name="slug" placeholder="slug" aria-label="Slug" required pattern="[a-z0-9][a-z0-9-]*" />
          <input name="displayName" placeholder="Display name" aria-label="Display name" required />
          <input
            name="packKey"
            placeholder="pack key (home_services)"
            aria-label="Pack key"
            defaultValue="home_services"
            required
          />
          <select name="planKey" defaultValue="" aria-label="Plan">
            <option value="">no plan yet</option>
            {plans.map((p) => (
              <option key={p.key} value={p.key}>
                {p.key}
              </option>
            ))}
          </select>
          <button className="primary" type="submit">
            Create
          </button>
        </form>
        <p className="muted tiny">
          Manual creation until Hub provisioning covers all cases — most tenants arrive via the Hub handshake.
        </p>
      </div>

      <div className="panel">
        <h2>Plans (seeded from packs — read-only)</h2>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Key</th>
                <th>Name</th>
                <th>Level</th>
                <th>Monthly</th>
                <th>Setup</th>
                <th>Hub product</th>
              </tr>
            </thead>
            <tbody>
              {plans.map((p) => (
                <tr key={p.id}>
                  <td className="mono">{p.key}</td>
                  <td>{p.name}</td>
                  <td>{p.level}</td>
                  <td>${(p.monthlyPriceCents / 100).toLocaleString()}</td>
                  <td>${(p.setupPriceCents / 100).toLocaleString()}</td>
                  <td className="mono">{p.hubProductSlug}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="panel">
        <h2>Users (Hub mirror — stub-mode creation only)</h2>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void createUser(new FormData(e.currentTarget));
          }}
          className="row"
          style={{ marginBottom: 12 }}
        >
          <input name="email" type="email" placeholder="email" aria-label="Email" required />
          <input name="name" placeholder="name" aria-label="Name" required />
          <select name="realm" defaultValue="ops" aria-label="Realm">
            <option value="ops">ops</option>
            <option value="client">client</option>
          </select>
          <button type="submit">Add user</button>
        </form>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Email</th>
                <th>Name</th>
                <th>Realm</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id}>
                  <td>{u.email}</td>
                  <td>{u.name}</td>
                  <td>{u.realm}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
